import {
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import {
  ExceptionGroupRow,
  ExceptionGroupRowProps,
} from "./ExceptionsGroupRow";
import { ExceptionGroup } from "../types";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useState } from "react";
import {
  ExceptionReviewAction,
  ListExceptionGroupsOrder,
  ListExceptionGroupsOrderField,
  OrderDirection,
} from "~/operations";
import { Link as RouterLink } from "react-router-dom";
import { ScopeType, spaceIdFromMrn, useScope } from "~/hooks/useScope";
import { DataTable } from "~/components/data-table";
import { ScopeProvider } from "~/providers/ScopeProvider";
import { spaceMrnFromExceptionGroup } from "~/lib/mrn";

type AllExceptionsProps = {
  allExceptionGroups: ExceptionGroup[];
  handleReviewActionChange: ExceptionGroupRowProps["onReviewActionChange"];
  exceptionId: ExceptionGroup["id"] | null;
  orderBy: ListExceptionGroupsOrder;
  sortColumnUrl: (field: string) => URL;
  isReviewLoading: boolean;
  loadingAction: ExceptionReviewAction | undefined;
  loadingExceptionId: string | undefined;
  refetch?: () => void;
};

export function AllExceptionList({
  allExceptionGroups,
  handleReviewActionChange,
  exceptionId,
  orderBy,
  sortColumnUrl,
  isReviewLoading = false,
  loadingAction,
  loadingExceptionId,
  refetch,
}: AllExceptionsProps) {
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const { activeScope } = useScope();
  const isPlatformScope = activeScope?.type === ScopeType.Region;

  const exceptionGroupRows = allExceptionGroups;

  const onExpandAllClick = () => {
    if (isAllExpanded) {
      setExpanded([]);
    } else {
      setExpanded(exceptionGroupRows.map((eg) => eg.exceptionId || eg.id));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  return (
    <DataTable
      sx={{
        bgcolor: "background.paper",
        borderRadius: 1,
        overflow: "hidden",
        "& tr:last-child td": {
          borderBottom: "none",
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Exception ID</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>
            <TableSortLabel
              component={RouterLink}
              to={sortColumnUrl(ListExceptionGroupsOrderField.CreatedAt).href}
              direction={
                orderBy.direction === OrderDirection.Asc ? "asc" : "desc"
              }
              active={orderBy.field === ListExceptionGroupsOrderField.CreatedAt}
            >
              Created
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel
              component={RouterLink}
              to={sortColumnUrl(ListExceptionGroupsOrderField.ValidUntil).href}
              direction={
                orderBy.direction === OrderDirection.Asc ? "asc" : "desc"
              }
              active={
                orderBy.field === ListExceptionGroupsOrderField.ValidUntil
              }
            >
              Expires
            </TableSortLabel>
          </TableCell>
          <TableCell align="right">
            <Button
              variant="outlined"
              endIcon={isAllExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
              sx={{ whiteSpace: "nowrap" }}
              onClick={onExpandAllClick}
            >
              Expand All
            </Button>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {exceptionGroupRows.map((eg) => {
          const isFocused =
            eg.exceptionId === exceptionId || eg.id === exceptionId;
          const isOpen =
            isFocused || expanded.includes(eg.exceptionId || eg.id);
          const onToggle: ExceptionGroupRowProps["onToggle"] = (
            exceptionId,
          ) => {
            if (isAllExpanded) {
              setIsAllExpanded(false);
            }
            if (expanded.includes(exceptionId)) {
              setExpanded(expanded.filter((id) => id !== exceptionId));
            } else {
              setExpanded([...expanded, exceptionId]);
            }
          };

          const row = (
            <ExceptionGroupRow
              key={eg.exceptionId || eg.id}
              exceptionGroup={eg}
              onReviewActionChange={handleReviewActionChange}
              isOpen={isOpen}
              isFocused={isFocused}
              onToggle={onToggle}
              isReviewLoading={isReviewLoading}
              loadingAction={loadingAction}
              loadingExceptionId={loadingExceptionId}
              extensions={eg.extensions || []}
              refetch={refetch}
              displayScope={isPlatformScope}
            />
          );

          if (isPlatformScope) {
            const spaceMrn = spaceMrnFromExceptionGroup(eg);
            const spaceId = spaceIdFromMrn(spaceMrn);
            return <ScopeProvider params={{ spaceId }}>{row}</ScopeProvider>;
          }

          return row;
        })}
      </TableBody>
    </DataTable>
  );
}
